/*
This file is located in the main branch

Last time the file was updated (UTC): [02.07.2024 18:53:04]
Permissions to update outside the main branch: [true]
*/

import { Form, Modal } from '@finbeat/react-toolkit';
import useAuth from 'hooks/useAuth';
import { TradeOrgAdd, TradeOrgChange } from 'models/api/tradeOrg';
import { Dispatch, Key, SetStateAction, useMemo, useState } from 'react';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useTradeOrgWorkplaceState } from '../TradeOrgs';
import TradeOrgChangeModalForm from './TradeOrgChangeModalForm';
import './TradeOrgModal.scss';

type TradeOrgChangeModalProps = {
    isOpen?: boolean;
    toggle: Dispatch<SetStateAction<boolean>>;
};

export type TradeOrgAddForm = TradeOrgAdd | TradeOrgChange;
function appendFormData(formData: FormData, data: any, parentKey = ''): void {
    const dataType = Object.prototype.toString.call(data);

    switch (dataType) {
        case '[object Null]':
        case '[object Undefined]':
            break;
        case '[object Boolean]':
            formData.append(parentKey, data ? 'true' : 'false');
            break;
        case '[object String]':
        case '[object Number]':
            if (data) {
                formData.append(parentKey, data.toString());
            }
            break;

        case '[object Array]':
            (data as any[]).forEach((item, index) => {
                const key = `${parentKey}[${index}]`;
                appendFormData(formData, item, key);
            });
            break;
        case '[object Object]':
            if (Object.keys(data).length === 0) {
                // Не добавляем пустой объект
                break;
            }
            Object.entries(data).forEach(([key, value]) => {
                const newKey = parentKey ? `${parentKey}.${key}` : key;
                appendFormData(formData, value, newKey);
            });
            break;
        case '[object File]':
            formData.append(parentKey, data);
            break;
        default:
            console.warn(`Unsupported data type in FormData: ${dataType} for key ${parentKey}`);
            break;
    }
}
function tradeOrgToFormData(tradeOrg: TradeOrgAddForm): FormData {
    const formData = new FormData();
    appendFormData(formData, tradeOrg);
    return formData;
}

function base64ToFile(base64: string, fileName: string, mimeType = 'application/octet-stream'): File | null {
    if (!base64 || base64.trim() === "") {
        return null;
    }

    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    return new File([blob], fileName, { type: mimeType });
}

const TradeOrgChangeModal = ({ isOpen, toggle }: TradeOrgChangeModalProps) => {
    const { currentItem, changeItemMutation, addItemMutation } = useTradeOrgWorkplaceState();
    const { hasRole } = useAuth();
    const { cultures } = useGeneralSettingsContext();

    const initialItem: TradeOrgAddForm = {
        isActive: true,
        isLandingShows: true,
        logoFile: null,
        backgroundFile: null,
        legalEntityName: '',
        brandName: '',
        askShipType: true,
        checkShip: true,
        integrationType: 'TO',
        loanStatus: 'goodship',
        notifyConfirmedOrder: null,
        paymentStatus: 'goodship',
        productCategory: '',
        questionaryId: null,
        url: null,
        dsc: '',
        integrators: {
            chooseIntegratorsFlag: false,
            list: [
                {
                    tradeOrgId: null,
                },
            ],
        },
        settings: {
            isUseDefault: true,
            list: [
                {
                    id: null,
                    type: '',
                    value: '',
                },
            ],
        },
        apiSettings: {
            id: null,
            login: '',
            password: '',
            proxySettings: {
                authenticationMethod: '',
                ip: '',
                login: '',
                password: '',
                port: null,
            },
            useHttpHeaders: false,
            useProxy: false,
            useOrderActualization: false,
            httpHeaders: [
                {
                    header: '',
                    value: '',
                },
            ],
            url: '',
        },
        tariffs: [],
        contactFaces: [],
        descriptions: cultures.reduce((a, v) => ({ ...a, [v.code]: '' }), {}),
    };

    const [activeTab, setActiveTab] = useState('main');

    const showAdditionalFields = useMemo(
        () => hasRole('tradeOrgsChangeAddtionalFields'),
        [hasRole]
    );

    const saveChanges = async (tradeOrg: TradeOrgAddForm) => {
        if (tradeOrg.settings) {
            tradeOrg.settings.list.forEach((i) => (i.value = i.value.toString()));
        }

        const formData = tradeOrgToFormData(tradeOrg);

        if ('id' in tradeOrg) {
            changeItemMutation.mutate(formData);
        } else {
            addItemMutation.mutate(formData);
        }

        toggle(false);
    };

    const closeModal = () => {
        toggle(false);
    };

    const toggleTab = (value?: Key) => {
        setActiveTab(value?.toString() ?? '');
    };

    const defaultValue: TradeOrgAddForm = useMemo(() => {
        if (currentItem) {
            const logoFile = base64ToFile(currentItem.logoFile, 'logofile.png', 'image/png');            
            const backgroundFile = base64ToFile(currentItem.backgroundFile, 'backgroundFile.png', 'image/png');

            return {
                ...initialItem,
                ...currentItem,
                logoFile: logoFile,
                backgroundFile: backgroundFile,
                logoFilePath: logoFile && URL.createObjectURL(logoFile),
                backgroundFilePath: backgroundFile && URL.createObjectURL(backgroundFile)
            } as TradeOrgChange;
        }
        return initialItem;
    }, [currentItem]);

    return (
        <Modal size="lg" isOpen={isOpen} toggle={closeModal} scrollable>
            <Form<TradeOrgAddForm>
                className="form-in-modal"
                onSubmit={(data) => saveChanges(data)}
                defaultValues={defaultValue}
                clearAfterSubmit={false}
            >
                <TradeOrgChangeModalForm
                    closeModal={closeModal}
                    currentActiveTab={activeTab}
                    toggleTab={toggleTab}
                    showAdditionalFields={showAdditionalFields}
                />
            </Form>
        </Modal>
    );
};

export default TradeOrgChangeModal;
